import { Badge, Box, Flex, ScrollArea, Spinner, Text } from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { RegressionListResponse } from "./types";
import { formatRunDate } from "./format";

interface RunSidebarProps {
  listData: RegressionListResponse | undefined;
  loading: boolean;
  selectedDatasetId: string | null;
  selectedRunId: string | null;
  onSelect: (datasetId: string, runId: string) => void;
}

function statusColor(
  status: string | undefined
): "green" | "red" | "amber" | "gray" {
  switch (status) {
    case "succeeded":
      return "green";
    case "failed":
      return "red";
    case "running":
      return "amber";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
}

export function RunSidebar({
  listData,
  loading,
  selectedDatasetId,
  selectedRunId,
  onSelect,
}: RunSidebarProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const suites = useMemo(() => listData?.suites ?? [], [listData]);

  return (
    <Flex
      direction="column"
      style={{
        width: 280,
        borderRight: "1px solid var(--gray-5)",
        background: "var(--gray-1)",
        flexShrink: 0,
        height: "100%",
        minHeight: 0,
      }}
    >
      <Flex
        direction="column"
        gap="2"
        px="4"
        py="4"
        style={{ borderBottom: "1px solid var(--gray-5)" }}
      >
        <Text size="3" weight="bold">
          Simulation tests
        </Text>
      </Flex>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Flex direction="column" gap="1" px="2" py="3">
          {loading ? (
            <Flex justify="center" py="4">
              <Spinner />
            </Flex>
          ) : null}
          {!loading && suites.length === 0 ? (
            <Text size="2" color="gray" style={{ padding: 12 }}>
              No datasets found
            </Text>
          ) : null}
          {suites.map((suite) => {
            const isCollapsed = collapsed[suite.id] ?? false;
            const visibleRuns = suite.runs;
            return (
              <Box key={suite.id}>
                <Flex
                  align="center"
                  gap="1"
                  px="2"
                  py="2"
                  onClick={() => toggle(suite.id)}
                  style={{
                    cursor: "pointer",
                    borderRadius: 6,
                  }}
                >
                  {isCollapsed ? (
                    <ChevronRightIcon />
                  ) : (
                    <ChevronDownIcon />
                  )}
                  <Text size="2" weight="medium">
                    {suite.name}
                  </Text>
                  <Badge color="gray" variant="soft" size="1" ml="auto">
                    {visibleRuns.length}
                  </Badge>
                </Flex>
                {!isCollapsed ? (
                  <Flex direction="column" gap="1" pl="5" pr="2">
                    {visibleRuns.length === 0 ? (
                      <Text size="1" color="gray" style={{ padding: 6 }}>
                        No runs
                      </Text>
                    ) : null}
                    {visibleRuns.map((run) => {
                      const isSelected =
                        selectedDatasetId === suite.id &&
                        selectedRunId === run.id;
                      return (
                        <Box
                          key={run.id}
                          onClick={() => onSelect(suite.id, run.id)}
                          style={{
                            cursor: "pointer",
                            background: isSelected
                              ? "var(--accent-4)"
                              : "transparent",
                            borderRadius: 6,
                            padding: "6px 10px",
                          }}
                        >
                          <Flex align="center" gap="2" justify="between">
                            <Text
                              size="1"
                              weight={isSelected ? "medium" : "regular"}
                              color={isSelected ? undefined : "gray"}
                            >
                              {formatRunDate(run.startedAt ?? run.name)}
                            </Text>
                            {run.status ? (
                              <Badge
                                color={statusColor(run.status)}
                                variant="soft"
                                size="1"
                              >
                                {run.status}
                              </Badge>
                            ) : null}
                          </Flex>
                        </Box>
                      );
                    })}
                  </Flex>
                ) : null}
              </Box>
            );
          })}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
